<template>
  <router-link
    activeClass="inspection-card--active"
    class="inspection-card"
    :to="{
      name: routePathName,
      params: { inspectionId: inspection.id, scope: inspectionScope },
    }"
  >
    <div
      ref="card"
      class="inspection-card__body"
    >
      <div class="inspection-card__header">
        <p class="typo-ignore typo-small-title inspection-card__title">
          {{ inspection.inspected_by && inspection.inspected_by.full_name }}
          <span
            v-if="inspection.inspection_type"
            class="inspection-card__type"
          >
            {{ inspectionTypeText }}
          </span>
        </p>
        <!-- Inspection status -->
        <p
          :class="`typo-ignore typo-small-title inspection-card__status inspection-card__status--${inspection.status}`"
        >
          <PrEngBadge
            v-if="inspection.professional_engineer"
            class="icon"
          />
          <component :is="statusIcon[inspection.status]" />
        </p>
      </div>
      <!-- Short Id / Issue count -->
      <div class="inspection-card__info">
        <p class="typo-ignore typo-tiny inspection-card__id">
          #{{ inspection.id && shortId }}
          <span
            v-if="inspection.issues"
            class="inspection-card__issues"
          >
            {{
              pluralize(
                inspection.issues.length,
                $t('activerecord.models.plants/issue.one'),
                $t('activerecord.models.plants/issue.other')
              )
            }}
          </span>
        </p>
        <p class="typo-ignore typo-tiny inspection-card__date">
          {{ formatDate(inspection.date) }}
        </p>
      </div>
      <!-- Issue dots, modified in colour using rating level -->
      <div
        v-if="inspection.issues"
        class="inspection-card__issue-summary"
      >
        <div
          v-for="(rating, index) in conditionRatings"
          :key="index"
          :class="`inspection-card__issue inspection-card__issue--${rating}`"
        />
      </div>
    </div>
  </router-link>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue';
import I18n from 'i18n-js';
import { useRoute } from 'vue-router';

// Utils
import pluralize from '@/functions/pluralize';
import { renderShortId } from '@/functions/utils.js';
import { isInViewport } from '@/functions/utils';
import { formatDate } from '@/functions/date_format_helpers';
import getRiskRatingLevel from '@/functions/simm/get_risk_rating_level';

// Icons
import PrEngBadge from '@/images/rosette_16.svg';
import CircleCheckmark16Svg from '@/images/circle-checkmark-16.svg';
import ViewFinderDot16Svg from '@/images/viewfinder-dot-16.svg';
import CircleDot16Svg from '@/images/circle-dotted-16.svg';

const props = defineProps({
  inspection: { type: Object, required: true },
  pathName: { type: String, required: false, default: null },
});

const route = useRoute();

/**
 * Status icons
 */
const statusIcon = {
  approved: CircleCheckmark16Svg,
  in_progress: ViewFinderDot16Svg,
  awaiting_approval: CircleDot16Svg,
};

const card = ref(null);

/**
 * Returns I18n text for inspection types
 */
const inspectionTypeText = computed(() => {
  return I18n.t(`inspection_types.${props.inspection.inspection_type}.name`);
});

const inspectionScope = computed(() => {
  return props.inspection.status === 'approved' ? 'approved_inspections' : 'inspections';
});

const shortId = computed(() => renderShortId(props.inspection.id));

const conditionRatings = computed(() => {
  if (!props.inspection?.issues) return [];

  if (route.name.includes('shaftV2'))
    return props.inspection.issues
      .map((i) => i.risk_rating)
      .sort((a, b) => b - a)
      .map(getRiskRatingLevel);

  return props.inspection.issues
    .map((i) => i?.activity?.overall_condition_rating)
    .sort((a, b) => b - a);
});

const routePathName = computed(() => {
  return props.pathName || 'inspection';
});

watchEffect(() => {
  if (card.value && route.name === 'inspection' && route.params.inspectionId === props.inspection.id) {
    if (!isInViewport(card.value)) {
      card.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
});
</script>
